import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTheme } from "@mui/material/styles";
import { updateUploadLawsuit } from "../../services/LawsuitService";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import Tooltip from "@mui/material/Tooltip";
import CardHeader from "../General/CardHeader";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import FileUploadBox from "../General/FileUploadBox";
import GlassCard from "../General/GlassCard";
import { useToast } from "../../context/ToastContext";

export default function ChargeUpdateLawsuit() {
  const { showToast } = useToast();
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ file: "" });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [actionType, setActionType] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);

  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  // Adaptado para recibir el archivo desde el componente Drag & Drop
  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          file: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        setForm({ ...form, file: "" });
        return;
      }

      setErrors({ ...errors, file: "" });
      setSelectedFile(file);
      setForm({ ...form, file: file.name });
    }
  };

  const handleConfirmUpdate = () => {
    setOpenConfirm(false);
    handleSubmit();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrors({});
    setLoading(true);

    if (!actionType) {
      showToast("Debe seleccionar una acción.", "info")
      setLoading(false);
      return;
    }

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info")
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      let response;

      if (actionType === "update") {
        response = await updateUploadLawsuit(formData);
      }

      showToast(response.data.message, "success")
      setForm({ file: "" });
      setSelectedFile(null);
      setErrors({});

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error")
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error")
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (actionType === "update") {
      setOpenConfirm(true);
    } else {
      handleSubmit();
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Actualizar Títulos (JEX)"
            description="Actualizar títulos que son necesarios en (JEX)."
          />

          <Box component="form" onSubmit={handleFormSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Acción</InputLabel>
                  <Select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    label="Acción"
                  >
                    <MenuItem value="">Seleccionar una opcion...</MenuItem>
                    <MenuItem value="update">Actualizar Títulos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Archivo Excel"
                  helperText="O arrastrá y soltá el archivo aquí (.xls, .xlsx)"
                />

                {errors.file && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading}
                 
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : actionType === "update" ? (
                    "Actualizar Títulos"
                  ) : (
                    "Ejecutar Acción"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleConfirmUpdate}
        title="Confirmar actualización"
        description="¿Estás seguro de que quieres actualizar estos títulos? Esta acción no se puede deshacer."
        confirmText="Actualizar"
        cancelText="Cancelar"
        confirmColor="primary"
        loading={loading}
      />

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={[
          "Codigo",
          "Tipo",
          "Numero de Orden",
          "Identificacion",
          "Cuit",
          "Nombre/Razon social",
          "Apellido (Opcional si es empresa)",
          "Tipo de Persona",
          "Monto (1000000.56)",
          "Decreto",
          "Fecha",
          "Constribuccion",
          "Direccion",
          "JucId (Opcional)",
          "Código del Estado del Sistema (Opcional, ver Configuración → Estados del Sistema)",
        ]}
        example={[
          "Aut/123/0000",
          "Automotor",
          "10072024",
          "OFM1234",
          "27123456755",
          "MARIA",
          "EJEMPLO",
          "F",
          "66367.03",
          "1007/2024",
          "18/03/2025",
          "CONTRIBUCIÓN QUE INCIDE SOBRE LOS AUTOMOTORES",
          "EJEMPLO -CP 00000",
          "3000",
          "5",
        ]}
      />
    </Grid>
  );
}
